import EmberObject from '@ember/object';

import * as OpAmpNegCurrent from './noise-sources/op-amp-neg-current';
import * as OpAmpPosCurrent from './noise-sources/op-amp-pos-current';
import * as OpAmpVoltage from './noise-sources/op-amp-voltage';
import * as R1 from './noise-sources/r1';
import * as R2 from './noise-sources/r2';
import * as R3 from './noise-sources/r3';
import * as R4 from './noise-sources/r4';
import FilterConstants from '../../../../filter-constants';

export default class SallenKeyLowPassNoiseSource extends EmberObject {
  r1 = null;
  r2 = null;
  r3 = null;
  r4 = null;
  c1 = null;
  c2 = null;
  opAmp = null;
  iNoise = null;
  vNoise = null;
  whoAmI = 'app/models/implementation/sallen-key/low-pass/noise-source.js';
  _r1Noise(filterAmplitude, complexDenominator, complexRParallel) {
    var baseAmplitudeCopy = filterAmplitude;
    var r1 = this.r1;
    var complexAlphaPlus = R1.computeComplexAlphaPlus(complexDenominator);
    var complexFF = R1.computeComplexFF(complexRParallel, r1);
    var value = Math.sqrt(r1 / 1000) * FilterConstants.resistorNoiseBase;
    var magnitude = baseAmplitudeCopy.computeMagnitude(complexAlphaPlus, complexFF);
    var result = value * magnitude;
    return result;
  }

  _r2Noise(filterAmplitude, complexDenominator, complexRParallel, sImaginary) {
    var baseAmplitudeCopy = filterAmplitude;
    var c1 = this.c1;
    var r1 = this.r1;
    var r2 = this.r2;
    var complexAlphaPlus = R2.computeComplexAlphaPlus(complexDenominator, c1, r1, sImaginary);
    var complexFF = R2.computeComplexFF(complexRParallel, r2);
    var value = Math.sqrt(r2 / 1000) * FilterConstants.resistorNoiseBase;
    var magnitude = baseAmplitudeCopy.computeMagnitude(complexAlphaPlus, complexFF);
    var result = value * magnitude;
    return result;
  }

  _r3Noise(filterAmplitude, complexDenominator, complexRParallel) {
    var baseAmplitudeCopy = filterAmplitude;
    var r3 = this.r3;
    var r4 = this.r4;
    var complexAlphaPlus = R3.computeComplexAlphaPlus(r3, r4);
    var complexFF = R3.computeComplexFF(complexRParallel, r3, r4);
    var value = Math.sqrt(r3 / 1000) * FilterConstants.resistorNoiseBase;
    var magnitude = baseAmplitudeCopy.computeMagnitude(complexAlphaPlus, complexFF);
    var result = value * magnitude;
    return result;
  }

  _r4Noise(filterAmplitude, complexDenominator, complexRParallel) {
    var baseAmplitudeCopy = filterAmplitude;
    var r3 = this.r3;
    var r4 = this.r4;
    var complexAlphaPlus = R4.computeComplexAlphaPlus(r3, r4);
    var complexFF = R4.computeComplexFF(complexRParallel, r3, r4);
    var value = Math.sqrt(r4 / 1000) * FilterConstants.resistorNoiseBase;
    var magnitude = baseAmplitudeCopy.computeMagnitude(complexAlphaPlus, complexFF);
    var result = value * magnitude;
    return result;
  }

  _opAmpNegCurrent(filterAmplitude, complexDenominator, complexRParallel) {
    var baseAmplitudeCopy = filterAmplitude;
    var r3 = this.r3;
    var r4 = this.r4;
    var complexAlphaPlus = OpAmpNegCurrent.computeComplexAlphaPlus(r3, r4);
    var complexFF = OpAmpNegCurrent.computeComplexFF(complexRParallel, r3, r4);
    var value = this.opAmp.iNoise;
    var magnitude = baseAmplitudeCopy.computeMagnitude(complexAlphaPlus, complexFF);
    var result = value * magnitude;
    return result;
  }

  _opAmpPosCurrent(filterAmplitude, complexDenominator, complexRParallel, sImaginary) {
    var baseAmplitudeCopy = filterAmplitude;
    var c1 = this.c1;
    var r1 = this.r1;
    var r2 = this.r2;
    var complexAlphaPlus = OpAmpPosCurrent.computeComplexAlphaPlus(complexDenominator, c1, r1, r2, sImaginary);
    var complexFF = OpAmpPosCurrent.computeComplexFF();
    var value = this.iNoise;
    var magnitude = baseAmplitudeCopy.computeMagnitude(complexAlphaPlus, complexFF);
    var result = value * magnitude;
    return result;
  }

  _opAmpVoltage(filterAmplitude /*, complexDenominator, complexRParallel*/) {
    var baseAmplitudeCopy = filterAmplitude;
    var complexAlphaPlus = OpAmpVoltage.computeComplexAlphaPlus();
    var complexFF = OpAmpVoltage.computeComplexFF();
    var value = this.vNoise;
    var magnitude = baseAmplitudeCopy.computeMagnitude(complexAlphaPlus, complexFF);
    var result = value * magnitude;
    return result;
  }

  totalNoise(filterAmplitude, complexDenominator, complexRParallel, sImaginary) {
    var r1Noise = this._r1Noise(filterAmplitude, complexDenominator, complexRParallel);
    var r2Noise = this._r2Noise(filterAmplitude, complexDenominator, complexRParallel, sImaginary);
    var r3Noise = this._r3Noise(filterAmplitude, complexDenominator, complexRParallel);
    var r4Noise = this._r4Noise(filterAmplitude, complexDenominator, complexRParallel);
    var opAmpNegCurrent = this._opAmpNegCurrent(filterAmplitude, complexDenominator, complexRParallel);
    var opAmpPosCurrent = this._opAmpPosCurrent(filterAmplitude, complexDenominator, complexRParallel, sImaginary);
    var opAmpVoltage = this._opAmpVoltage(filterAmplitude, complexDenominator, complexRParallel);
    var noiseArray = [r1Noise, r2Noise, r3Noise, r4Noise, opAmpNegCurrent, opAmpPosCurrent, opAmpVoltage];
    var sumOfSquares = 0;

    noiseArray.forEach((item) => {
      sumOfSquares += item * item;
    });

    var result = Math.sqrt(sumOfSquares);
    return result;
  }
}
